/* =========================================================================
 * THE RESOLVE MODEL — pure, and a MIRROR of the server's own validator.
 *
 * Brian: "if there is contradicting information there can be a flag, but if it
 * says there are multiple winners, there needs to be a new winner created. Also
 * if we have to manually type the winner in because that's the only source then
 * we should be able to manually input it."
 *
 * All three already existed on sr-portal-backend (`resolve.js#checkResolution`)
 * and nothing in this app had ever called `/api/champion-resolve`. So the
 * capability was real and faceless — same as the sweep button.
 *
 * WHY MIRROR THE VALIDATION RATHER THAN JUST POSTING.
 * The server's refusals are specific and useful ("a manual entry needs a basis
 * — how you know"), but finding them out by submitting means a round trip and a
 * red box for something the form could have said while it was being typed. This
 * checks the same rules up front. It is NOT the enforcement — the server
 * re-decides every one of these, and a client check that claims to secure
 * anything is a lie this codebase has written down before (see RBAC.md).
 *
 * ⚠️ IF THIS DRIFTS FROM resolve.js, THE FORM STARTS LYING. Every rule below
 * names the server rule it mirrors so a change there is findable from here.
 * ========================================================================= */

/** The five things that can be true about a championship. */
export const OUTCOMES = ['crowned', 'not_yet', 'split', 'not_found', 'manual']

export const OUTCOME_META = {
  manual: {
    /* ⚠️ "No link needed" CONTRADICTED THE VALIDATION AND I INTRODUCED THAT.
     * The house rules now require a link OR a basis that names how the rep
     * knows, so a rep who read the old blurb, typed "I know who won" and got
     * refused was being told two different things by one screen. The blurb now
     * says what the form will actually accept. */
    label: 'I know the winner',
    blurb: 'You were there, the coach told you, or the school posted it. A link is best — '
      + 'without one, say HOW you know.',
  },
  split: {
    label: 'There are several winners',
    blurb: 'This event crowns one champion per division. Each division becomes its own championship record.',
  },
  not_found: {
    label: 'Searched, nothing found',
    blurb: 'Tag it so nobody searches it again tomorrow, and so it is findable as needing a person.',
  },
}

const S = (v) => String(v ?? '').trim()

/**
 * Mirrors `resolve.js#checkResolution`. Returns the same shape the server does:
 * a list of problems, empty when it is submittable.
 *
 * The wording is deliberately close to the server's, so somebody who hits a
 * server-side refusal recognises it rather than thinking two different systems
 * disagreed about them.
 */
export function checkResolution(r = {}) {
  const out = []
  const outcome = S(r.outcome).toLowerCase()
  if (!OUTCOMES.includes(outcome)) out.push(`outcome must be one of: ${OUTCOMES.join(', ')}`)
  if (!S(r.id)) out.push('id is required')
  /* The reason IS the audit trail, which is why 15 characters rather than 1.
   * "fixed" tells the next person nothing. */
  if (S(r.reason).length < 15) out.push('reason must actually say something — it is the audit trail')

  if (outcome === 'manual') {
    if (!S(r.champion)) out.push('a manual entry needs a champion')
    /* No source URL required, on purpose: a rep who was at the game has better
     * information than any page. But a manual champion with no basis is just a
     * rumour with a name on it. */
    if (S(r.basis).length < 10) {
      out.push('a manual entry needs a basis — how you know. Attended, told by the coach, saw the school post it, read the bracket.')
    }
  }

  if (outcome === 'split') {
    const divs = Array.isArray(r.divisions) ? r.divisions : []
    if (divs.length < 2) out.push('a split needs at least two divisions — one division is just a crown')
    if (!/^https?:\/\//i.test(S(r.sourceUrl))) out.push('a split needs a source URL naming the divisions')
    const seen = new Set()
    divs.forEach((d, i) => {
      const name = S(d?.division)
      if (!name) out.push(`division ${i + 1} has no name`)
      const k = slug(name)
      if (k && seen.has(k)) out.push(`division "${name}" appears twice`)
      seen.add(k)
    })
  }

  return out
}

/* =========================================================================
 * THE HOUSE RULES — SR's crown bar, which is STRICTER than the server's.
 *
 * Brian: *"If our system doesn't find the champion, the rep still needs to be
 * able to manually hunt for it after asking the system to hunt and properly
 * input the champion."* And: *"A crown is champion + SOURCE + runId + ACTOR +
 * TIMESTAMP in ONE refusable write. 63 sourceless crowns were once minted by a
 * path that wrote a subset."*
 *
 * ⚠️ THESE ARE DELIBERATELY NOT PART OF `checkResolution`, AND THAT SEPARATION
 * IS THE POINT. That function is a MIRROR of `resolve.js#checkResolution`, and
 * its whole value is that a refusal a rep meets on the server is one the form
 * already showed them in the same words. Folding a rule the server does NOT
 * have into the mirror would make the mirror a lie — a rep would be refused
 * here for something that would have been accepted there, with no way to tell
 * which half spoke.
 *
 * So: `checkResolution` says what the SERVER will take. `houseRules` says what
 * SR will PUT ITS NAME ON. Both run, and each problem knows which it came
 * from. The server remains the enforcement for its own rules; these are ours
 * and are enforced here because the server does not have them yet.
 * ========================================================================= */

/* =========================================================================
 * 🔴 NO CHAMPION ON A CHAMPIONSHIP THAT HAS NOT BEEN DECIDED.
 *
 * Brian: "There should be no future records recorded unless it's been decided
 * already."
 *
 * ⚠️ THIS IS THE DEFECT THE LIVE TEST ALMOST COMMITTED. I had eight correct
 * 2025 NCHSAA football champions in hand and every NCHSAA football record in
 * the object is dated 2026-12-12 — the UPCOMING occurrence. Entering Reidsville
 * there would have put last season's winner on a future event: a plausible,
 * sourced, correctly-spelled champion on the wrong record, which is the single
 * hardest kind of error to find afterwards. The old rule only asked whether the
 * date I TYPED was in the past. It was. Nothing compared it to the record.
 *
 * So the question is not "is the entered date in the past". It is HAS THIS
 * CHAMPIONSHIP BEEN DECIDED, and the record's own event date answers it.
 *
 * ⭐ THE ONE EXCEPTION IS REAL AND IT IS BRIAN'S STANDING RULE: a STANDINGS
 * title can be clinched before its final event. "nascar canada did crown a
 * season champ. even if its before the final race… same style as the socon
 * with football" — and "It's no different than the SoCon football championship
 * happening in week 8." Chevrolet clinched IMSA GTD PRO at Petit Le Mans;
 * Ducati clinched WorldSBK four rounds early.
 *
 * ⚠️ BUT THE EXCEPTION IS A SECOND DELIBERATE STEP, NOT A CHECKBOX THAT WAVES
 * THE REFUSAL AWAY. The refusal comes first and names both dates; only then can
 * the rep assert a clinch, and asserting it costs a source showing the title is
 * mathematically settled. A tick-box would be read as a formality and the trap
 * would re-open within a week.
 * ========================================================================= */

/** The record's own event date, whatever shape the normalizer left it in. */
export function recordEventDate(record) {
  const raw = record?.effDate ?? record?.eventDate ?? record?.raw?.sr_event_date
    ?? record?.sr_event_date ?? null
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw
  const d = readDay(raw)
  return d ? d.date : null
}

/** `'past'` (a champion is expected) · `'future'` (not yet decided) · `'undated'`. */
export function decidedState(record, { now = new Date() } = {}) {
  const d = recordEventDate(record)
  if (!d) return 'undated'
  /* Compared at DAY granularity: an event today has been decided today, and a
   * record dated this morning must not refuse because the clock says 14:00. */
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const ev = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return ev > day ? 'future' : 'past'
}

const fmt = (d) => (d instanceof Date
  ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  : 'no date')

/** `YYYY-MM-DD`, and a real date rather than a well-shaped string. */
export function readDay(v) {
  const t = S(v)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null
  const [y, m, d] = t.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
  return { y, m, d, date: dt }
}

/**
 * The SR bar for a champion a person is putting on the record.
 *
 * `record` is the championship being crowned; it decides whether a division is
 * demanded, because only the record knows whether this event HAS divisions.
 */
export function houseRules(r = {}, record = null, { now = new Date() } = {}) {
  const out = []
  const outcome = S(r.outcome).toLowerCase()
  /* Only the two outcomes that put a NAME on a record. `not_found` and
   * `not_yet` assert nothing about a winner, so none of this applies — and
   * demanding a source for "I looked and it is not out there" would make the
   * honest answer the expensive one, which is how records stop being tagged. */
  if (outcome !== 'manual' && outcome !== 'crowned') return out

  /* 🔴 A SOURCE, MANDATORY — a URL *or* a stated citation.
   *
   * Brian: *"'I know who won' is not a source. This is the field that makes
   * the record defensible to a customer."* The server requires a `basis` of 10
   * characters on a manual entry, which "I know who won" (14) clears. So the
   * house rule is not a longer string, it is a check that the basis says HOW
   * the rep knows rather than THAT they know. */
  const url = S(r.sourceUrl)
  const hasUrl = /^https?:\/\/\S+\.\S+/i.test(url)
  const basis = S(r.basis)
  if (!hasUrl && !basis) {
    out.push('a champion needs a source — paste the link, or say how you know it')
  } else if (!hasUrl && !statesHowTheyKnow(basis)) {
    out.push('say HOW you know, not that you know — "I was at the final", "the AD '
      + 'told me on the phone", "the school posted it on their Facebook". A customer '
      + 'has to be able to be shown why this name is on the ring.')
  }
  if (url && !hasUrl) out.push('that source does not look like a link — it needs to start with http:// or https://')

  /* ⚠️ THE YEAR IS IDENTITY, NOT METADATA. The same championship is run every
   * season; a champion with no season attached cannot be told from the one
   * before it, and the hunt's single most dangerous failure is a repeat
   * champion lifted from last year's recap — the name checks out and every
   * sanity check passes. */
  const day = readDay(r.eventDate)
  if (!S(r.eventDate)) out.push('the date this was decided is required — the YEAR is what tells this title from last season’s')
  else if (!day) out.push('that date is not a real date — it needs to be YYYY-MM-DD')
  else if (day.date > now) out.push('a crown says it already happened, so the date cannot be in the future')

  /* 🔴 THE DIVISION, WHERE THE EVENT HAS THEM, AND THE REP PICKS IT.
   *
   * Brian: *"The wrong division is worse than a blank — one cheer event has 226
   * divisions, and IMSA GTD PRO crowned THREE different winners in 2026
   * (manufacturer, drivers, teams). Make the rep pick, do not infer."*
   *
   * ⚠️ AND IT IS ONLY DEMANDED WHEN THE RECORD SAYS THERE ARE DIVISIONS.
   * Demanding one of every record would make a rep invent a division on a
   * single-title event, which is the same fabrication in the other direction. */
  /* 🔴 THE RECORD'S OWN DATE DECIDES WHETHER A CHAMPION MAY BE RECORDED. */
  const state = decidedState(record, { now })
  if (state === 'future') {
    const evd = fmt(recordEventDate(record))
    const typed = day ? fmt(day.date) : 'the date you entered'
    if (!r.earlyClinch) {
      out.push(`This championship has not happened yet — the record is dated ${evd}, and you `
        + `entered ${typed}. A champion cannot be recorded before the event is decided. If this `
        + 'is a STANDINGS title that has already been clinched, say so and show where it was '
        + 'declared.')
    } else if (!/^https?:\/\/\S+\.\S+/i.test(S(r.clinchSourceUrl))) {
      /* ⚠️ THE CLINCH COSTS A LINK, AND A BASIS DOES NOT SUBSTITUTE HERE.
       * Everywhere else a stated channel is an acceptable source, because a rep
       * who was at the final saw it with their own eyes. Nobody watches a title
       * get clinched mathematically — it is declared, or it is arithmetic
       * somebody did. The governing body's own declaration is the thing to
       * link, and demanding it is what keeps this from becoming the tick-box
       * that re-opens the trap. */
      out.push('An early clinch needs a link to where it was declared — the governing body’s '
        + 'standings or its announcement. Prefer the body’s own declaration over anyone’s '
        + 'arithmetic.')
    }
  }

  const divisions = recordDivisions(record)
  if (divisions.length > 0 && !S(r.division)) {
    out.push(`this event crowns ${divisions.length} divisions — pick which one this champion won`)
  }
  if (divisions.length > 0 && S(r.division)
    && !divisions.some((d) => slug(d) === slug(r.division))) {
    out.push(`"${S(r.division)}" is not one of this event’s divisions`)
  }

  return out
}

/**
 * ⚠️ WHAT TO SAY BUT NOT REFUSE ON.
 *
 * 🔴 THE SEASON FIELD IS KNOWN-UNRELIABLE AND MUST NOT BLOCK A WRITE. The PLL
 * record crowned in the live test carries `sr_event_date` 2025-09-14 against
 * `SEASON 2026`, and the drawer itself already flags it "date disputed".
 * Refusing on a field that is wrong on real records turns a safety rail into a
 * wall: every correct write on a disputed record would be blocked by the
 * dispute. So the disagreement is NAMED and the rep decides.
 *
 * The event date is different — it is what the record is FOR — so that one
 * refuses, above.
 */
export function houseWarnings(r = {}, record = null) {
  const out = []
  const outcome = S(r.outcome).toLowerCase()
  if (outcome !== 'manual' && outcome !== 'crowned') return out

  const day = readDay(r.eventDate)
  const ev = recordEventDate(record)
  if (day && ev) {
    const gap = Math.abs(day.date - ev) / 86400000
    /* A few days apart is a festival running over a weekend, or a recap
     * datelined the morning after — not a disagreement worth a sentence. */
    if (gap > 45) {
      out.push(`The date you entered (${fmt(day.date)}) is ${Math.round(gap)} days from the `
        + `record's own event date (${fmt(ev)}). Check this is the right record — the same `
        + 'championship is run every season and they look identical apart from the year.')
    }
  }

  const season = Number(record?.seasonYear ?? record?.raw?.sr_season ?? NaN)
  if (day && Number.isFinite(season)) {
    /* SR files a season Jul-Jun, so a date in Sep 2025 belongs to season 2025. */
    const entered = day.m >= 7 ? day.y : day.y - 1
    if (entered !== season) {
      out.push(`The record says SEASON ${season} but the date you entered falls in the `
        + `${entered}-${entered + 1} season. The season field is unreliable on this object — `
        + 'this is worth a look, not a blocker.')
    }
  }
  return out
}

/* A basis that names a CHANNEL — where the knowledge came from — rather than
 * merely asserting it. Deliberately generous: the job is to stop "I know who
 * won", not to grade a rep's prose. A sentence long enough to carry a real
 * account passes on length alone. */
const CHANNEL = /\b(attend|was at|were at|watch|saw|told|call|phone|spoke|email|text|message|post|facebook|instagram|twitter|x\.com|announce|bracket|program|printed|scoreboard|press|paper|news|broadcast|stream|coach|athletic director|\bad\b|official|banquet|ceremony|presented)/i

export function statesHowTheyKnow(basis) {
  const t = S(basis)
  if (t.length >= 60) return true
  return CHANNEL.test(t)
}

/** The divisions a record declares, or `[]` when it crowns a single title. */
export function recordDivisions(record) {
  const raw = record?.divisions ?? record?.sr_divisions ?? null
  if (Array.isArray(raw)) return raw.map((d) => S(d?.division ?? d)).filter(Boolean)
  const t = S(raw)
  if (!t) return []
  return t.split(/[|,;\n]/).map(S).filter(Boolean)
}

/* =========================================================================
 * PROVENANCE — rep-entered vs machine-resolved, permanently.
 *
 * ⚠️ THERE IS NO `crowned_by` FIELD, AND I AM NOT INVENTING ONE. What exists
 * is `champion_locked`, and it carries the answer for a real reason rather
 * than by luck: `POST /api/champion-update` answers **403
 * `automation_forbidden`** to a service identity on that field — measured, and
 * recorded in CLAUDE.md. Only a PERSON can set it. No engine, no grant and no
 * scheduled job can. So a locked crown is a human crown, and that is a
 * property of the system rather than a convention somebody could drift.
 *
 * It is also why a rep entry is written as `outcome: 'manual'`: `manual` is
 * the outcome that locks. A `crowned` write would be a safe create the hunt
 * could not overwrite either — but it would leave NOTHING on the record saying
 * a person put the name there, and an unreadable provenance is the failure
 * Brian named explicitly: once it is gone, nothing recovers it.
 *
 * `unknown` is a real answer and is rendered as one. A record crowned before
 * this path existed cannot be attributed now, and guessing "machine" would
 * manufacture provenance rather than report it.
 * ========================================================================= */
export const PROVENANCE = {
  rep: {
    key: 'rep',
    label: 'Entered by a person',
    why: 'A person put this name on the record and locked it. No automated hunt can overwrite it — '
      + 'a later machine run that disagrees raises a conflict for a human instead.',
  },
  machine: {
    key: 'machine',
    label: 'Resolved by the hunt',
    why: 'The champion hunt found this and recorded its source. A person can correct it.',
  },
  unknown: {
    key: 'unknown',
    label: 'Origin not recorded',
    why: 'This crown predates the record carrying who put it there, so it cannot be attributed now. '
      + 'It is not evidence either way.',
  },
}

export function crownProvenance(record) {
  if (!record || !S(record.champion)) return null

  /* 🔴 THE FIELD EXISTS AND I BUILT AN INFERENCE BESIDE IT.
   *
   * This function used to derive human-vs-machine from `champion_locked`,
   * documented at length on the reasoning that no provenance field existed.
   * The live write proved otherwise: the record came back carrying
   * `champion_provenance: "human_entered"` and `champion_entered_by:
   * "Brian Kass"`. The inference happened to agree — which is the dangerous
   * case, because a derivation that agrees today drifts silently tomorrow.
   *
   * So the FIELD is read first and the inference is demoted to a fallback for
   * records written before the backend carried it. Nothing is guessed when the
   * record can simply say. */
  const how = S(record.championProvenance ?? record.raw?.champion_provenance).toLowerCase()
  if (how === 'human_entered' || how === 'human') return PROVENANCE.rep
  if (how === 'hunt' || how === 'machine' || how === 'auto') return PROVENANCE.machine

  /* ⚠️ FALLBACK ONLY. `champion_locked` is still a sound signal — a service
   * identity is answered 403 `automation_forbidden` on that field, so only a
   * person can set it — but it answers a narrower question than the field
   * does, and it is used only where the field is silent. */
  if (record.championLocked === true) return PROVENANCE.rep
  if (S(record.championSourceUrl)) return PROVENANCE.machine
  return PROVENANCE.unknown
}

/** Who put the name there, when the record says. '' when it does not. */
export function crownEnteredBy(record) {
  return S(record?.championEnteredBy ?? record?.raw?.champion_entered_by)
}

/* =========================================================================
 * THE RUN ID AND THE ACTOR — the other half of the one refusable write.
 *
 * `runId` is in the endpoint's contract and this client has never sent one, so
 * every resolve written from this app has landed with no run to trace it to.
 * A rep entry composes one that names the path and the person.
 * ========================================================================= */
export function repRunId(actor, { now = new Date() } = {}) {
  const who = slug(S(actor?.name) || S(actor?.email) || S(actor) || 'unknown-rep')
  return `rep-entry:${who}:${now.toISOString()}`
}

/**
 * The actor, stated in the audit trail itself rather than only in a header.
 * The server derives identity from the verified JWT and that is the one that
 * counts; this makes the trail readable without joining to a token.
 */
export function withActor(reason, actor) {
  const who = S(actor?.name) || S(actor?.email) || ''
  const t = S(reason)
  if (!who) return t
  return `${t} — entered by ${who}`
}

/** The server slugs division names to build the child record key. Same rule. */
export function slug(v) {
  return S(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

/**
 * Read a resolve failure into something a person can act on.
 *
 * The three that matter are all 409s carrying structure, and flattening them to
 * "failed" throws away the entire point — a disagreement is a FINDING, not an
 * error.
 */
export function readResolveError(err) {
  const b = err?.body || {}
  if (b.error === 'champion_disagreement') {
    return {
      kind: 'conflict',
      existing: S(b.existing),
      proposed: S(b.proposed),
      message: b.reason || 'This record already names a different champion.',
    }
  }
  /* THE SAME QUESTION, ASKED BY A DIFFERENT ROUTE.
   *
   * POST /api/champion-request refuses a record that already names a champion,
   * because putting a finished record back in the hunt queue is how a good
   * crown gets overwritten. That refusal is not a fault — it is the record
   * telling you it already has an answer, and the only useful next move is the
   * same one a resolve conflict offers: look at the name and correct it if it
   * is wrong.
   *
   * `proposed` is deliberately EMPTY here. Nothing was found to weigh against
   * the existing champion, and manufacturing a second name to fill the shape
   * would report a disagreement that nobody observed. The panel branches on it. */
  if (b.error === 'already_crowned') {
    return {
      kind: 'conflict',
      existing: S(b.champion),
      proposed: '',
      message: b.reason || 'This record already names a champion.',
    }
  }
  if (b.error === 'champion_locked') {
    return { kind: 'locked', message: b.reason || 'A human locked this record.' }
  }
  if (b.error === 'invalid_resolution') {
    return { kind: 'invalid', problems: Array.isArray(b.problems) ? b.problems : [] }
  }
  return { kind: 'error', message: b.reason || b.error || err?.message || 'The resolution failed.' }
}

/**
 * Turn a hunt result into the divisions a split would create.
 *
 * The hunt returns several champions for one record when the event crowns one
 * per division — which is the case Brian is describing. Nothing is invented: a
 * division the source did not name a winner for gets its record and stays
 * uncrowned, exactly as the server allows. Creating the record is discovery;
 * inventing its champion would be fabrication.
 */
export function divisionsFromFinds(finds = []) {
  return finds
    .map((f) => ({ division: S(f?.division) || S(f?.segment) || S(f?.name), champion: S(f?.champion) }))
    .filter((d) => d.division)
}
